import type {
  HistoryDays,
  TeamCheckHistoryRowDto,
} from '@internal/scorecards-common';
import { useEffect, useReducer } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { scorecardsApiRef } from '../api/ScorecardsApi';

type State = {
  loading: boolean;
  error?: Error;
  rows?: TeamCheckHistoryRowDto[];
};
type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; rows: TeamCheckHistoryRowDto[] }
  | { type: 'FETCH_ERROR'; error: Error }
  | { type: 'IDLE' };

function reducer(_state: State, action: Action): State {
  switch (action.type) {
    case 'FETCH_START':
      return { loading: true };
    case 'FETCH_SUCCESS':
      return { loading: false, rows: action.rows };
    case 'FETCH_ERROR':
      return { loading: false, error: action.error };
    case 'IDLE':
      return { loading: false };
    default:
      return _state;
  }
}

// Lazy hook: only fetches when checkId is non-null (i.e., a row is expanded).
// Starts idle (loading: false) so no spinner appears before the user expands a row.
export function useTeamCheckHistory(
  team: string,
  checkId: string | null,
  days: HistoryDays,
): { loading: boolean; error?: Error; rows?: TeamCheckHistoryRowDto[] } {
  const api = useApi(scorecardsApiRef);
  const [state, dispatch] = useReducer(reducer, { loading: false });

  useEffect(() => {
    if (!checkId) {
      dispatch({ type: 'IDLE' });
      return undefined;
    }

    let cancelled = false;
    dispatch({ type: 'FETCH_START' });

    api
      .getTeamCheckHistory(team, checkId, days)
      .then(rows => {
        if (!cancelled) dispatch({ type: 'FETCH_SUCCESS', rows });
      })
      .catch(err => {
        if (!cancelled)
          dispatch({
            type: 'FETCH_ERROR',
            error: err instanceof Error ? err : new Error(String(err)),
          });
      });

    return () => {
      cancelled = true;
    };
  }, [api, team, checkId, days]);

  return state;
}
